import { UserProfileDTO } from '@/api/auth';

const INACTIVITY_PROFILE_KEY = 'inactivity_logout_profile';

/**
 * The profile the user worked in when logged out for inactivity, so the
 * profiles page can select it again after login. During "Visa som" it also
 * holds what "Tillbaka" needs: the profile and page the user came from.
 */
export interface InactivityProfile {
  profileId: string;
  previousProfileId?: string;
  preImpersonationLocation?: string;
}

export interface ProfileSelection {
  profile: UserProfileDTO;
  previousProfile?: UserProfileDTO;
  preImpersonationLocation?: string;
}

export const rememberInactivityProfile = (state: InactivityProfile): void => {
  localStorage.setItem(INACTIVITY_PROFILE_KEY, JSON.stringify(state));
};

export const getInactivityProfile = (): InactivityProfile | null => {
  try {
    const stored = JSON.parse(
      localStorage.getItem(INACTIVITY_PROFILE_KEY) ?? 'null',
    );
    return typeof stored?.profileId === 'string' ? stored : null;
  } catch {
    return null;
  }
};

export const forgetInactivityProfile = (): void => {
  localStorage.removeItem(INACTIVITY_PROFILE_KEY);
};

/**
 * A "Visa som" profile is restored while it is valid and the profile it was
 * opened from is still selectable. Otherwise the user lands in the profile
 * they impersonated from, or in their remembered regular profile.
 */
export const findProfileToRestore = (
  remembered: InactivityProfile | null,
  profiles: UserProfileDTO[],
  selectable: UserProfileDTO[],
  tenantId: string | undefined,
  now: Date = new Date(),
): ProfileSelection | null => {
  if (!remembered) {
    return null;
  }

  const inTenant = (profile: UserProfileDTO) =>
    !tenantId || profile.tenant_id === tenantId;
  const previousProfile = selectable.find(
    (profile) =>
      profile.id === remembered.previousProfileId && inTenant(profile),
  );
  const impersonation = profiles.find(
    (profile) =>
      profile.id === remembered.profileId &&
      !!profile.expires_at &&
      new Date(profile.expires_at) > now &&
      inTenant(profile),
  );

  if (impersonation && previousProfile) {
    return {
      profile: impersonation,
      previousProfile,
      preImpersonationLocation: remembered.preImpersonationLocation,
    };
  }

  const profile =
    selectable.find(
      (profile) => profile.id === remembered.profileId && inTenant(profile),
    ) ?? previousProfile;
  return profile ? { profile } : null;
};
